/**
 * Shared hook for fetching scenario pricing data for a specific hospital.
 * Computes blended prices (hospital matched codes + national avg for unmatched).
 * Includes phase breakdown data and i18n translations.
 * Used by MapBottomPanel, MapRightPanel (HospitalScenariosTab), and HospitalProfile.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { batchedIn } from '@/lib/batchedQuery';
import { SCENARIO_CATEGORIES, scenarioMatchesCategory } from '@/config/scenarioCategories';
import { useTranslatedContentBatch } from '@/hooks/useTranslatedContent';
import { loadStaticData } from '@/lib/offlineData';
import { isGarbagePrice } from '@/lib/priceSanity';

export interface HospitalScenarioRow {
  scenario_id: string;
  total_hospital_price: number;
  total_national_price: number;
  avg_markup_percentage: number;
  coverage_percentage: number;
  matched_codes: number;
  total_codes: number;
  /** 'drg' = inpatient core priced from the hospital's DRG bundle (else itemized). */
  pricing_method?: string;
  /** USA rank for this hospital within this scenario. 1 = most expensive in country. Populated by workflow/compute_scenario_usa_rank.sql */
  usa_rank_expensive: number | null;
  /** USA rank for this hospital within this scenario. 1 = cheapest in country. */
  usa_rank_cheap: number | null;
  /** Total qualifying hospitals for this scenario (denominator for the USA rank). */
  usa_total_ranked: number | null;
  /** Percentile rank 0-100. 0 = cheapest, 100 = most expensive. */
  percentile_rank: number | null;
  scenario: {
    name: string;
    category: string;
    icon_name: string | null;
    estimated_cost: number | null;
    tags: string[];
    description: string | null;
    slug: string | null;
    severity_level: number | null;
    duration: string | null;
    total_procedures: number | null;
    phases: string[];
    phaseBreakdown: Record<string, number>;
    /** When set, this scenario is one variant in a family — siblings share the same family_id but differ in severity_level. */
    scenario_family_id?: string | null;
    family_variant_label?: string | null;
  };
  nationalCost: number;
  blendedPrice: number;
  blendedMarkup: number;
}

export interface UseHospitalScenariosResult {
  scenarios: HospitalScenarioRow[];
  cheapestAll: HospitalScenarioRow[];
  mostExpensiveAll: HospitalScenarioRow[];
  byCategory: Record<string, HospitalScenarioRow[]>;
  totalCount: number;
  isLoading: boolean;
  /** Get translated field value, falling back to English */
  getTranslation: (scenarioId: string, field: string, fallback: string) => string;
}

export function useHospitalScenarios(hospitalId: string | undefined): UseHospitalScenariosResult {
  const { data: rawScenarios = [], isLoading: dataLoading } = useQuery({
    queryKey: ['hospital-scenarios', hospitalId],
    queryFn: async () => {
      // 1. Try static JSON first (R2-backed, CDN-cached per-hospital file with short keys)
      try {
        const staticData = await loadStaticData<{ h: string; n: number; s: any[] }>(
          `hospital_scenarios/${hospitalId}.json`
        );
        if (staticData?.s?.length) {
          // Load scenarios index once to enrich pricing rows with metadata (name, description, phases, etc.)
          const idx = await loadStaticData<{ data: any[] }>('scenarios_index.json');
          const scenarioMeta = new Map<string, any>();
          if (idx?.data) {
            for (const s of idx.data) {
              scenarioMeta.set(s.id || s.uuid, s);
            }
          }

          return staticData.s.map((row): HospitalScenarioRow => {
            const meta = scenarioMeta.get(row.s) || {};
            const phases = Array.isArray(meta.phases)
              ? meta.phases.map((p: any) => typeof p === 'string' ? p : (p?.name || String(p)))
              : [];
            return {
              scenario_id: row.s,
              total_hospital_price: row.hp,
              total_national_price: row.np,
              avg_markup_percentage: row.mp,
              coverage_percentage: row.cp,
              matched_codes: row.mc,
              total_codes: row.tc,
              pricing_method: row.pm,
              // Static cache rank fields — populated once `generate_hospital_detail_cache.py`
              // is rerun after the USA rank UPDATE. Until then they're null and the live
              // DB fallback below will fill them.
              usa_rank_expensive: row.re ?? null,
              usa_rank_cheap: row.rc ?? null,
              usa_total_ranked: row.rt ?? null,
              percentile_rank: row.pr ?? null,
              nationalCost: row.nc,
              blendedPrice: row.bp,
              blendedMarkup: row.bm,
              scenario: {
                name: meta.name || 'Unknown Scenario',
                category: meta.category || 'other',
                icon_name: meta.iconName || meta.icon_name || null,
                estimated_cost: meta.estimatedCost ?? meta.estimated_cost ?? null,
                tags: Array.isArray(meta.tags) ? meta.tags : [],
                description: meta.description || null,
                slug: meta.slug || null,
                severity_level: meta.severityLevel ?? meta.severity_level ?? null,
                duration: meta.duration || null,
                total_procedures: meta.totalProcedures ?? meta.total_procedures ?? null,
                phases,
                phaseBreakdown: meta.phaseBreakdown || {},
                scenario_family_id: meta.scenarioFamilyId ?? meta.scenario_family_id ?? null,
                family_variant_label: meta.familyVariantLabel ?? meta.family_variant_label ?? null,
              },
            };
          });
        }
      } catch { /* fall through to DB */ }

      // 2. Fallback: live DB query — mirrors generate_static_cache.py's
      // generate_hospital_scenarios() row selection (coverage>=50 OR
      // cornerstone_pass, active scenarios only, pricing_method included).
      const { data, error } = await (supabase as any)
        .from('scenario_hospital_coverage')
        .select(`
          scenario_id, total_hospital_price, total_national_price,
          avg_markup_percentage, coverage_percentage, matched_codes, total_codes,
          pricing_method,
          usa_rank_expensive, usa_rank_cheap, usa_total_ranked, percentile_rank,
          scenarios!inner(name, category, icon_name, estimated_cost, tags, description, slug, severity_level, duration, total_procedures, phases, scenario_family_id, family_variant_label, is_active)
        `)
        .eq('hospital_id', hospitalId!)
        .eq('scenarios.is_active', true)
        .gt('total_hospital_price', 0)
        // Cornerstone AND coverage: a hospital must have the scenario's
        // DEFINING procedure and at least half its codes to be listed.
        // Matching only 1/8 incidental codes (labs, imaging) is not "offering
        // this scenario" — it produced junk "$18 uterus transplant" cards.
        .eq('cornerstone_pass', true)
        .gte('coverage_percentage', 50);

      if (error) { console.error('Hospital scenarios error:', error); return []; }
      const rows = (data || []) as any[];
      if (rows.length === 0) return [];

      // Fetch national costs + phase_costs in one batch
      const ids = rows.map((r: any) => r.scenario_id);
      const { data: costs } = await batchedIn(
        'scenario_costs_cache',
        'scenario_id',
        ids,
        'scenario_id, real_total_cost, phase_costs'
      );
      const costMap = new Map((costs || []).map((c: any) => [c.scenario_id, c]));

      // Compute blended prices and attach phase data
      return rows.map((r: any): HospitalScenarioRow => {
        const costRow = costMap.get(r.scenario_id);
        const nationalCost = costRow ? Number(costRow.real_total_cost) : 0;

        // If the hospital covers 100% of codes, use its prices directly — no imputation.
        // drg_blended rows are already complete (DRG bundle covers the inpatient core),
        // so imputing unmatched-national on top would double-count the stay.
        // Otherwise, blend hospital's matched prices with national avg for unmatched codes.
        const isFullyCovered = r.matched_codes && r.total_codes && r.matched_codes >= r.total_codes;
        // DB uses 'drg_blended'; static shards shorten to 'drg' — accept both.
        const isDrgBlended = r.pricing_method === 'drg_blended' || r.pricing_method === 'drg';
        const unmatchedNational = (isFullyCovered || isDrgBlended || nationalCost <= 0)
          ? 0
          : Math.max(0, nationalCost - r.total_national_price);
        const blendedPrice = r.total_hospital_price + unmatchedNational;
        const blendedMarkup = nationalCost > 0
          ? ((blendedPrice - nationalCost) / nationalCost) * 100
          : r.avg_markup_percentage;

        // Phase breakdown from scenario_costs_cache.phase_costs (precomputed national costs per phase)
        const phaseCosts = costRow?.phase_costs;
        const phases = Array.isArray(r.scenarios.phases) ? r.scenarios.phases : [];
        const phaseBreakdown: Record<string, number> = {};
        if (phaseCosts && typeof phaseCosts === 'object') {
          for (const [phase, cost] of Object.entries(phaseCosts)) {
            phaseBreakdown[phase] = Number(cost) || 0;
          }
        }

        return {
          scenario_id: r.scenario_id,
          total_hospital_price: r.total_hospital_price,
          total_national_price: r.total_national_price,
          avg_markup_percentage: r.avg_markup_percentage,
          coverage_percentage: r.coverage_percentage,
          matched_codes: r.matched_codes,
          total_codes: r.total_codes,
          pricing_method: r.pricing_method,
          usa_rank_expensive: r.usa_rank_expensive ?? null,
          usa_rank_cheap: r.usa_rank_cheap ?? null,
          usa_total_ranked: r.usa_total_ranked ?? null,
          percentile_rank: r.percentile_rank != null ? Number(r.percentile_rank) : null,
          scenario: {
            ...r.scenarios,
            phases,
            phaseBreakdown,
          },
          nationalCost,
          blendedPrice,
          blendedMarkup,
        };
      });
    },
    enabled: !!hospitalId,
  });

  // Batch-fetch translations for all scenario IDs
  const scenarioIds = useMemo(() => rawScenarios.map(r => r.scenario_id), [rawScenarios]);
  const { getTranslation, isLoading: translationsLoading } = useTranslatedContentBatch(
    'scenario',
    scenarioIds,
    ['name', 'description']
  );

  // Apply translations to scenario names/descriptions
  const scenarios = useMemo(() =>
    rawScenarios.map(row => ({
      ...row,
      // Preserve all top-level fields including the new rank fields
      scenario: {
        ...row.scenario,
        name: getTranslation(row.scenario_id, 'name', row.scenario.name),
        description: getTranslation(row.scenario_id, 'description', row.scenario.description || ''),
      },
    })),
    [rawScenarios, getTranslation]
  );

  // Don't block on translations — they're a progressive enhancement
  const isLoading = dataLoading;

  // Sanity guard: filter scenarios where the NATIONAL AVERAGE is clearly inflated
  // (broken HCPCS J-code unit pricing producing $1M+ averages). Only suppress when
  // nationalCost itself is in the millions — this lets through legitimate cheap-hospital
  // prices (e.g. a hospital chargemastering $3,552 for a lung transplant vs a real $352K
  // national avg) while still blocking comparisons against a broken $11.4M IVIG baseline.
  // Shared garbage-price rules — see src/lib/priceSanity.ts for the policy.
  // The coverage guard also runs CLIENT-side because static shards generated
  // before the cornerstone-AND-coverage server filter (2026-07-06) still
  // contain low-coverage rows until the next R2 refresh.
  const sanitizedScenarios = useMemo(
    () => scenarios.filter(r => {
      if ((r.coverage_percentage ?? 0) < 50) return false;
      return !isGarbagePrice(r.blendedPrice, r.nationalCost, r.blendedMarkup);
    }),
    [scenarios]
  );

  /** All scenarios sorted by best value (lowest markup %) */
  const cheapestAll = useMemo(
    () => [...sanitizedScenarios].sort((a, b) => a.blendedMarkup - b.blendedMarkup),
    [sanitizedScenarios]
  );

  /** All scenarios sorted by worst value (highest markup %) */
  const mostExpensiveAll = useMemo(
    () => [...sanitizedScenarios].sort((a, b) => b.blendedMarkup - a.blendedMarkup),
    [sanitizedScenarios]
  );

  const byCategory = useMemo(() => {
    const map: Record<string, HospitalScenarioRow[]> = {};
    for (const cat of SCENARIO_CATEGORIES) {
      if (cat.key === 'all') continue;
      const matched = sanitizedScenarios.filter((r) => {
        // Match by tags (primary) OR by scenario.category field (fallback for DRG base etc.)
        if (scenarioMatchesCategory(r.scenario.tags || [], cat)) return true;
        const scenCat = r.scenario.category?.toLowerCase() || '';
        const browseKey = cat.browseLabel.toLowerCase();
        return scenCat === browseKey || scenCat.includes(cat.key);
      });
      if (matched.length > 0) {
        map[cat.key] = matched.sort((a, b) => a.blendedPrice - b.blendedPrice);
      }
    }
    return map;
  }, [scenarios]);

  return {
    scenarios: sanitizedScenarios,
    cheapestAll,
    mostExpensiveAll,
    byCategory,
    totalCount: sanitizedScenarios.length,
    isLoading,
    getTranslation,
  };
}
